import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    QueueConnectionState,
    QueueSnapshot,
    diagnoseQueue,
    formatAge,
    formatCount,
    formatDuration,
    stageLabel,
} from '../../api/queueMonitor';
import { Finding, WorkerFleet } from '../../api/health';
import { CollapsibleSection } from '../Section/CollapsibleSection';
import { QueueBoard } from './QueueBoard';
import { PoisonRequeuePanel } from './PoisonRequeuePanel';
import './QueueMonitor.scss';

export interface IQueueMonitorPanelProps {
    snapshot: QueueSnapshot | null;
    connection: QueueConnectionState;
    error: string | null;
    onRefresh: () => void;
    /**
     * The environment this panel belongs to. Only needed to put failed messages back; the
     * state itself comes from the snapshot.
     */
    customer?: string;
    environment?: string;
    /**
     * A queue to open, set from outside.
     *
     * The diagnosis panel above offers "put these back" next to the finding that explains
     * why they failed, and pressing it has to land on the messages themselves. Without this
     * the button could only tell the reader to go and find the right card, which is the
     * hunting this dashboard is meant to end.
     */
    focusQueue?: string | null;
    /** Called once a focus request has been honoured, so it does not fire twice. */
    onFocusHandled?: () => void;
    /**
     * The function apps of this environment, so the board can show what reads each queue,
     * plus the payment sync and whether the apps can be seen at all.
     *
     * Comes from the health call rather than from the snapshot because a verdict about an app
     * rests on evidence the snapshot does not carry. Absent, the board is drawn from the
     * snapshot alone, which already names every consumer, so nothing breaks while the health
     * call is still in flight.
     */
    fleet?: WorkerFleet | null;
    /**
     * The findings of this environment, so an app or a function can show that something is
     * wrong with it instead of only what its heartbeat says.
     */
    findings?: Finding[];
    /** Which app's telemetry panel is open, so its frame can show as pressed. */
    openWorker?: string | null;
    /** Opens the telemetry panel of one app. */
    onOpenWorker?: (appKey: string) => void;
    /** Whether the section is unfolded. Owned by the page, like every other section. */
    open: boolean;
    onToggle: () => void;
}

const CONNECTION_LABEL: Record<QueueConnectionState, string> = {
    idle: 'Nog niet verbonden',
    connecting: 'Verbinden',
    live: 'Live',
    reconnecting: 'Verbinding herstellen',
    polling: 'Elke 5 sec',
    offline: 'Geen verbinding',
};

const CONNECTION_HINT: Record<QueueConnectionState, string> = {
    idle: 'Kies een omgeving om de queues te volgen.',
    connecting: 'Bezig met verbinden.',
    live: 'De server stuurt elke wijziging meteen door.',
    reconnecting: 'De verbinding is weggevallen; er wordt opnieuw verbonden.',
    polling:
        'De directe verbinding komt er niet doorheen, dus de stand wordt elke vijf seconden opgehaald. De getallen kloppen, ze lopen alleen iets achter.',
    offline: 'De queuestand kon niet worden opgehaald.',
};

/**
 * From when the age of the last measurement is worth mentioning.
 *
 * The server only pushes a new state to the screen when the picture changes, and otherwise
 * sends a quiet environment a heartbeat every 20 seconds. On a quiet environment the age
 * of the last measurement therefore always climbs to twenty seconds or so - nothing is
 * wrong, there is just nothing new to report. That climbing counter next to "Live" read as
 * "the numbers are lagging", which is why on a live connection it only appears once it
 * passes the heartbeat: at that point something really is up.
 */
const STALE_AFTER_SECONDS = 25;

/**
 * The live state of the queues, drawn as the pipelines they actually are.
 *
 * Separate tiles per queue do not tell you where things get stuck; this view does, because
 * you can see which step the messages pile up in. Click a queue to see what is inside, or an
 * app to see what it has been doing.
 */
export const QueueMonitorPanel = (props: IQueueMonitorPanelProps) => {
    const [openQueue, setOpenQueue] = useState<string | null>(null);

    const focusRef = useRef<HTMLElement | null>(null);

    // The clock driving the ages ticks here rather than in the hook: there it would
    // re-render the entire page - including a table of hundreds of rows - every second for
    // a couple of words of text.
    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);

        return () => clearInterval(timer);
    }, []);

    const queues = useMemo(() => props.snapshot?.queues ?? [], [props.snapshot]);

    const { focusQueue, onFocusHandled } = props;

    useEffect(() => {
        if (!focusQueue) {
            return;
        }

        // Only once the queue is in the snapshot: opening a name that is not there yet
        // would leave the panel showing nothing and swallow the request.
        if (!queues.some((q) => q.name === focusQueue)) {
            return;
        }

        setOpenQueue(focusQueue);
        focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        onFocusHandled?.();
    }, [focusQueue, queues, onFocusHandled]);

    const ageSeconds = props.snapshot
        ? Math.max(
              0,
              Math.round(
                  (now - (Date.parse(props.snapshot.sampledAt) || now)) / 1000
              )
          )
        : 0;

    const open = useMemo(
        () => queues.find((q) => q.name === openQueue) ?? null,
        [queues, openQueue]
    );

    // Where the opened queue stands, in the same words as on the card.
    const openDiagnosis = useMemo(
        () => (open ? diagnoseQueue(open, now) : null),
        [open, now]
    );

    const loading = !props.snapshot && props.connection !== 'offline';

    /** Everything that is on a queue right now, so the folded header can say it. */
    const waiting = useMemo(
        () => queues.reduce((total, queue) => total + queue.depth, 0),
        [queues]
    );

    // On a live connection the age of the measurement is only news when it climbs too far;
    // once the screen falls back to polling, how old the numbers are should always be
    // stated.
    const stale = ageSeconds >= STALE_AFTER_SECONDS;
    const showAge = !!props.snapshot && (props.connection !== 'live' || stale);

    const toggle = (name: string) =>
        setOpenQueue((current) => (current === name ? null : name));

    return (
        <CollapsibleSection
            id="queue-monitor"
            className="queue-monitor"
            title="Queues"
            open={props.open}
            onToggle={props.onToggle}
            innerRef={focusRef}
            status={
                <>
                    <span
                        className={`queue-monitor__pulse queue-monitor__pulse--${props.connection}`}
                        title={CONNECTION_HINT[props.connection]}
                    >
                        <span className="queue-monitor__dot" />
                        {CONNECTION_LABEL[props.connection]}
                    </span>

                    {/*
                        Worth carrying into the folded header: a total of nothing waiting is
                        the whole answer, and it saves opening the section to find that out.
                    */}
                    {!loading && queues.length > 0 && (
                        <span
                            className="queue-monitor__total"
                            title="Alle berichten die nu op de queues van deze omgeving staan, mislukte berichten meegerekend."
                        >
                            {formatCount(waiting)} bericht(en) onderweg
                        </span>
                    )}

                    {showAge && (
                        <span
                            className={`queue-monitor__age ${
                                stale ? 'queue-monitor__age--stale' : ''
                            }`}
                            title={
                                stale
                                    ? 'De server heeft al een tijd geen nieuwe stand gestuurd. Deze getallen kunnen achterlopen; meet nu opnieuw om het zeker te weten.'
                                    : 'Hoe lang geleden deze getallen bij Azure zijn opgehaald.'
                            }
                        >
                            {stale ? 'laatste meting ' : 'gemeten '}
                            {formatDuration(ageSeconds)} geleden
                        </span>
                    )}
                </>
            }
            tools={
                <button
                    type="button"
                    className="queue-monitor__refresh"
                    onClick={props.onRefresh}
                    title="Nu opnieuw meten"
                >
                    <FontAwesomeIcon icon="rotate" />
                    <span className="visually-hidden">Nu opnieuw meten</span>
                </button>
            }
        >
            {props.error && (
                <p className="queue-monitor__error" role="alert">
                    {props.error}
                </p>
            )}

            {loading && (
                <p className="queue-monitor__placeholder">
                    <FontAwesomeIcon icon="spinner" spin /> queuestand ophalen...
                </p>
            )}

            {!loading && queues.length === 0 && !props.error && (
                <p className="queue-monitor__placeholder">
                    Deze omgeving heeft geen queues.
                </p>
            )}

            <QueueBoard
                queues={queues}
                fleet={props.fleet}
                findings={props.findings ?? []}
                selectedQueue={openQueue}
                onSelectQueue={toggle}
                openWorker={props.openWorker}
                onOpenWorker={props.onOpenWorker ?? (() => undefined)}
                now={now}
            />

            {open && (
                <div className="queue-peek">
                    <div className="queue-peek__head">
                        <strong>{stageLabel(open)}</strong>
                        <code className="queue-peek__name">{open.name}</code>
                        <span className="queue-peek__count">
                            {formatCount(open.depth)} bericht(en)
                        </span>

                        <button
                            type="button"
                            className="queue-peek__close"
                            onClick={() => setOpenQueue(null)}
                            title="Sluiten"
                        >
                            <FontAwesomeIcon icon="xmark" />
                        </button>
                    </div>

                    {/* The diagnosis is spelled out here and not just in a tooltip on the
                        card: this is the moment someone wants to know why these messages
                        are here and what to do about them. */}
                    {openDiagnosis && (
                        <p
                            className={`queue-peek__verdict queue-peek__verdict--${openDiagnosis.tone}`}
                        >
                            <strong>{openDiagnosis.label}.</strong>{' '}
                            {openDiagnosis.hint}
                        </p>
                    )}

                    {/* Only for a poison queue, and only when we know which step it
                        belongs to: without that we cannot tell where the messages go. */}
                    {open.stage === 'Poison' && open.poisonOf && (
                        <PoisonRequeuePanel
                            customer={props.customer}
                            environment={props.environment}
                            poisonQueue={open.name}
                            targetQueue={open.poisonOf}
                            depth={open.depth}
                            onMoved={props.onRefresh}
                        />
                    )}

                    {open.head.length === 0 ? (
                        <p className="queue-peek__empty">
                            {open.depth === 0
                                ? 'Deze queue is leeg.'
                                : /*
                                       Every message here is invisible, and Azure offers no
                                       way to look at one: reading a message requires a pop
                                       receipt, and you only get one by receiving a message
                                       that is visible. Saying "picked up" here was the
                                       comfortable reading of that silence, and the wrong
                                       one. What these messages are is knowable from another
                                       direction - they failed, so they are in the error log -
                                       which is where this now points.
                                    */
                                  'Alle berichten hier zijn onzichtbaar en daar geeft Azure geen inzage in: om een bericht te lezen is een pop receipt nodig, en die krijg je alleen bij een bericht dat zichtbaar is. Onzichtbaar betekent hier bijna altijd: een mislukte poging achter zich en wachten op de volgende. Wélke objecten dat zijn, staat in het foutenlogboek hieronder.'}
                        </p>
                    ) : (
                        <>
                            <table className="queue-peek__table">
                                <thead>
                                    <tr>
                                        <th>Bericht</th>
                                        <th>Staat er</th>
                                        <th>Mislukt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {open.head.map((message) => (
                                        <tr key={message.messageId}>
                                            <td>
                                                {message.relationId ? (
                                                    <>
                                                        <strong>
                                                            {message.relationId}
                                                        </strong>
                                                        <span className="queue-peek__type">
                                                            {message.objectType}
                                                            {message.eventType
                                                                ? ` · ${message.eventType}`
                                                                : ''}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <code title={message.body}>
                                                        {message.body}
                                                    </code>
                                                )}
                                            </td>
                                            <td>
                                                {formatAge(
                                                    message.insertedOn,
                                                    now
                                                ) ?? '-'}
                                            </td>
                                            <td>
                                                {message.dequeueCount === 0 ? (
                                                    <span className="queue-peek__muted">
                                                        nog niet geprobeerd
                                                    </span>
                                                ) : (
                                                    <span className="queue-peek__retry">
                                                        {message.dequeueCount}x
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {open.depth > open.head.length && (
                                <p className="queue-peek__more">
                                    De eerstvolgende {open.head.length} van{' '}
                                    {formatCount(open.depth)}. Azure laat alleen de
                                    kop van een queue zien.
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}
        </CollapsibleSection>
    );
};
